import { generalConfig } from '@sola/config/lib/getters.ts';
import {
  CartItemLegacyGroupEnum,
  CartItemTypeEnum,
} from '@sola/schema/dotcom/cart/cart-item.ts';
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import type { CartItemFund } from '@sola/schema/dotcom/cart/fund.ts';
import type { CartItemGift } from '@sola/schema/dotcom/cart/gift.ts';
import { SponsorshipItemTypeEnum } from '@sola/schema/dotcom/cart/sponsorship.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import type { FundProps } from '@sola/schema/lib/schema.ts';
import { mapCartItemTypeToLegacyGroup } from '@sola/shared-utils/analytics.ts';

const getFundCodeFrequency = (fund: FundProps) => {
  const defaultResult = {
    commitmentFrequency: CommitmentFrequencyEnum.OneTime,
    fundCode: fund.fundCodeOneTime,
  };
  if (fund.availableFrequencies?.includes(CommitmentFrequencyEnum.OneTime)) {
    return defaultResult;
  }

  if (!fund.availableFrequencies?.includes(CommitmentFrequencyEnum.OneTime)) {
    return {
      commitmentFrequency: CommitmentFrequencyEnum.Monthly,
      fundCode: fund.fundCodeCommitment || fund.fundCodeOneTime,
    };
  }

  return defaultResult;
};

export const convertFundToCart = (
  fund: FundProps,
  isCatalog?: boolean,
): Partial<CartItem> => {
  const { commitmentFrequency, fundCode } = getFundCodeFrequency(fund);
  return {
    itemType: CartItemTypeEnum.Fund,
    itemCode: fundCode,
    title: fund.fundTitle,
    // TODO: Ideally we'd be able to pull the default selection from a component
    commitmentFrequency: isCatalog
      ? CommitmentFrequencyEnum.OneTime
      : commitmentFrequency,
    unitPrice: isCatalog
      ? (fund.catalogAmount ?? 0)
      : fund.suggestedAmount ||
        fund.suggestedAmountsOneTime?.find((a) => a.isDefault)?.value ||
        fund.suggestedAmountsMonthly?.find((a) => a.isDefault)?.value ||
        fund.suggestedAmountsAnnually?.find((a) => a.isDefault)?.value ||
        0,
    quantity: 1,
    fund: {
      fundCode: fundCode || '',
    },
  };
};

type AnalyticsCartItemData = {
  category?: string;
  name?: string;
  group?: CartItemLegacyGroupEnum;
};

export type AnalyticsCartItem = {
  item_id: string;
  item_gid: string;
  item_category: string;
  item_category2: string;
  item_name: string;
  item_variant: string;
  price: number;
  quantity: number;
};

/**
 *  Get the cart item fund name for analytics events.
 * @param fundType The type of the fund.
 * @param fundName The name of the fund.
 */
const getFundName = (fundType: string, fundName: string): string => {
  switch (fundType) {
    case 'DS': {
      return 'Child Sponsorship';
    }
    case '12MD':
    case '12SB':
    case '12NU':
    case '12BB':
    case '12BN':
    case '12NC': {
      return 'Babies and Moms Fund';
    }
    case '12GW': {
      return 'Bibles Fund';
    }
    case '12DR':
    case '12SL':
    case '12DW':
    case '12BR':
    case '12HD':
    case '12OC':
    case '12FS':
    case '12EC': {
      return 'Children at Risk Fund';
    }
    case '12ED':
    case '12VT':
    case '12AR':
    case '12BA':
    case '12BE':
    case '12ET':
    case '12CM':
    case '12LE':
    case '12MC':
    case '12PG':
    case '12SE':
    case '12SP':
    case '12ST':
    case '12TB': {
      return 'College & Training Fund';
    }
    case 'CF': {
      return 'Christmas Gift Fund';
    }
    case 'CP': {
      return 'Critical Program Needs Fund';
    }
    case 'ER': {
      return 'Disaster Relief Fund';
    }
    case 'ED': {
      return 'Educational Needs Fund';
    }
    case 'GFC': {
      return 'Global Food Crisis Fund';
    }
    case 'NH': {
      return 'Health and Nutrition Fund';
    }
    case '12DC':
    case '12EF':
    case '12BH':
    case '12TR':
    case '12HK':
    case '12SU':
    case '12MV':
    case '12PA':
    case '12IL':
    case '12VA': {
      return 'Health Fund';
    }
    case 'OV': {
      return 'Highly Vulnerable Children Fund';
    }
    case 'HA': {
      return 'HIV and AIDS Initiative Fund';
    }
    case '12GF':
    case '12FB':
    case '12FP': {
      return 'Hunger Fund';
    }
    case 'IG':
    case '12BI':
    case '12LV':
    case '12CK':
    case '12CW':
    case '12GS':
    case '12GO':
    case '12PI':
    case '12BL': {
      return 'Income Generation Fund';
    }
    case 'DI': {
      return 'Infrastructure Development Fund';
    }
    case 'MP': {
      return 'Lifesaving Surgery Fund';
    }
    case 'MV': {
      return 'Malaria Intervention Fund';
    }
    case 'MA': {
      return 'Medical Assistance Fund';
    }
    case 'MX': {
      return 'Ministry Expansion Fund';
    }
    case 'FF': {
      return 'Partners of Compassion Fund';
    }
    case 'WP': {
      return 'Safe Water Sanitation Hygiene Fund';
    }
    case '5S': {
      return 'Survival Program Fund';
    }
    case 'UC':
    case '12UC': {
      return 'Unsponsored Children Fund';
    }
    case '12WB':
    case '12EL':
    case '12BW':
    case '12WF':
    case '12WI': {
      return 'Water and Energy Fund';
    }
    case 'MN':
    case '12MS': {
      return 'Where Most Needed Fund';
    }
    case '5Y': {
      return 'Youth Development Fund';
    }
    default: {
      return `${fundName} Fund`;
    }
  }
};

/**
 * Format cart type, name, and category for usage in analytics events
 * @param item The cart item data.
 */
const mapToAnalyticsData = (item: Partial<CartItem>): AnalyticsCartItemData => {
  const group = mapCartItemTypeToLegacyGroup(item);

  const getData = (): AnalyticsCartItemData | undefined => {
    switch (group) {
      case CartItemLegacyGroupEnum.ChildSelected: {
        return {
          name: item.title,
          category: item.child?.country || '',
        };
      }
      case CartItemLegacyGroupEnum.EPayment: {
        return {
          name: 'E Payment',
        };
      }
      case CartItemLegacyGroupEnum.FirstPayment: {
        return {
          name: 'First Payment',
        };
      }
      case CartItemLegacyGroupEnum.LongestWaiting: {
        return {
          name: 'Longest Waiting Child',
        };
      }
      case CartItemLegacyGroupEnum.ChildCriteria: {
        return {
          name: 'Child Matching Certain Criteria',
        };
      }
      case CartItemLegacyGroupEnum.CommitmentPayment: {
        return {
          // TODO: This seems like a bug in legacy - not all "CommitmentPayment"s are sponsorships...
          name: 'Sponsorship Payment',
        };
      }
      case CartItemLegacyGroupEnum.GiftPayment: {
        return {
          name: `${(item as CartItemGift).gift?.giftType || ''} Gift`,
          category: (item as CartItemGift).gift?.giftType || '',
        };
      }
      case CartItemLegacyGroupEnum.Fund: {
        return {
          name: item.title,
          category: getFundName(
            (item as CartItemFund).fund?.fundCode || '',
            item.title!,
          ),
        };
      }
    }
  };

  return {
    group,
    ...getData(),
  };
};

/**
 * Format cart items for analytics events.
 * @param items	Array of items to format.
 * @returns {array}
 */
export const mapAnalyticsCartItems = (
  items: Partial<CartItem>[],
): AnalyticsCartItem[] => {
  if (!items?.length) {
    return [];
  }

  return items.map((item): AnalyticsCartItem => {
    const { name, category, group } = mapToAnalyticsData(item);

    return {
      item_id: item.itemCode!,
      // TODO: Should we make item_gid optional?
      item_gid: item.child?.globalId || '',
      item_category: group || '',
      item_category2: category || '',
      item_name: name || item.title!,
      item_variant: item.commitmentFrequency || '',
      price: item.unitPrice!,
      quantity: item.quantity || 1,
    };
  });
};

export const convertChildToCart = (
  item: ChildAvailabilityItem,
): Partial<CartItem> => {
  return {
    // TODO: Ideally this would come from the SDG ContentSettings.fundCodeCdsp
    itemCode: 'DS',
    itemType: CartItemTypeEnum.Sponsorship,
    commitmentFrequency: CommitmentFrequencyEnum.Monthly,
    quantity: 1,
    sponsorship: {
      sponsorshipType: SponsorshipItemTypeEnum.Selected,
      globalId: item.globalId,
      sourceCode: item.sourceCode ?? undefined,
    },
    child: {
      name: item.name?.preferred,
      globalId: item.globalId,
      localId: item.localId,
      imageUrl: '',
      birthDate: '',
      country: item.fieldOffice,
    },
    // TODO: Currently a non-exported constant in cart's sponsorship.ts
    title: 'Child Sponsorship',
    // TODO: Ideally this would come from the SDG ContentSettings.defaultCdspRate or a subsidy...
    unitPrice: generalConfig.rate,
  };
};

export {
  mapSponsorshipTypeToLegacyGroup,
  mapCartItemTypeToLegacyGroup,
} from '@sola/shared-utils/analytics.ts';
